<script lang="ts">
	import { run } from "svelte/legacy"

	import { DataHandlerDevice, DataHandlerSnapshot, DeviceGroup } from "luxedo-data"
	import { getCurrentUser } from "../../../../../stores/UserStore.svelte"
	import { CollapsibleSection } from "misc-ui"
	import { ArrowDown } from "luxedo-icons"
	import { mobileScrollFade } from "responsive"

	type ResolutionList = Array<{ x: number; y: number }>

	interface Props {
		selectedSnapshotId: number
		selectedDeviceId: number
	}

	let { selectedSnapshotId, selectedDeviceId }: Props = $props()

	let suggestedResolutions: ResolutionList = $state([])

	function updateResolution() {
		suggestedResolutions = []
		const device = DataHandlerDevice.get(selectedDeviceId)

		if (device instanceof DeviceGroup) {
			suggestedResolutions.push({
				x: device.resX,
				y: device.resY,
			})

			if (getCurrentUser().hasPriv("commercial")) {
				suggestedResolutions.push({
					x: Math.ceil(device.resX / 2),
					y: Math.ceil(device.resY / 2),
				})
				suggestedResolutions.push({
					x: Math.ceil(device.resX / 4),
					y: Math.ceil(device.resY / 4),
				})
			}
		} else {
			const snapshot = DataHandlerSnapshot.get(selectedSnapshotId)
			if (snapshot === undefined) {
				return suggestedResolutions.push({
					x: device.resX,
					y: device.resY,
				})
			}

			suggestedResolutions.push({
				x: snapshot.resolution.w,
				y: snapshot.resolution.h,
			})
		}
	}

	function renderResolutions(resList: ResolutionList) {
		let str = ""
		for (let i = 0; i < resList.length; i++) {
			const res = resList[i]

			let seperator = ", "
			if (i === resList.length - 1) seperator = " or "
			else if (i === 0) seperator = ""

			str = `${str}${seperator}${res.x}x${res.y}`
		}
		return str
	}

	run(() => {
		;(selectedSnapshotId || selectedDeviceId) && updateResolution()
	})
</script>

<div class="flex-column" id="export-container" use:mobileScrollFade>
	<h3 class="upload-step">Export your project</h3>
	<p>Export your project, making sure that:</p>
	<ul>
		<li>Your snapshot image has been hidden before rendering your scene.</li>
		<li>
			The scene is being exported to the <span class="highlight">MP4</span> or
			<span class="highlight">MOV</span> format.
		</li>
		{#if suggestedResolutions.length === 1}
			<li>
				Your scene is being exported to the correct resolution <span class="highlight"
					>({suggestedResolutions[0].x ?? "0"}x{suggestedResolutions[0].y ?? "0"})</span
				>.
			</li>
		{:else}
			<li>
				Your scene is being exported to a valid resolution matching the aspect ratio <span
					class="highlight">({renderResolutions(suggestedResolutions)})</span
				>.
				<span style="font-weight: bold;"
					>The aspect ratio must match the original resolution and be no less than 1/4 of the
					resolution.</span
				>
			</li>
		{/if}
	</ul>

	<p>Once exported, move on to the next step to upload.</p>
	<p>If you are working with a professional, you can skip this step.</p>
	<br />
	<CollapsibleSection>
		{#snippet header()}
			<div class="more-info-heading">
				<span> More info </span>
				<ArrowDown />
			</div>
		{/snippet}
		{#snippet content()}
			<div class="more-info">
				<p>Supported codecs:</p>
				<ul>
					<li>MP4: H.264, H.265, VP9</li>
					<li>MOV: Apple ProRes</li>
					<li>MKV/AV1: coming soon!</li>
				</ul>
				<br />
			</div>
		{/snippet}
	</CollapsibleSection>
</div>

<style>
	p {
		margin: 1rem 0 0 0;
		color: var(--color-text);
	}

	.more-info p {
		margin-top: 0;
	}

	.highlight {
		color: var(--color-main);
	}

	ul {
		list-style: disc;
		margin-left: 1rem;
		color: var(--color-text);
	}

	.more-info-heading {
		display: flex;
		flex-direction: row;
		align-items: center;
		font-size: var(--h1);
		font-family: var(--font-heading);
	}

	.more-info-heading :global(svg) {
		height: 2rem;
		width: auto;
		transition: transform 250ms;
	}

	:global(.section-expanded) .more-info-heading :global(svg) {
		transform: rotateX(180deg);
	}

	.more-info-heading :global(.svg-stroke) {
		stroke: var(--color-main);
	}

	:global(.isMobile) .flex-column#export-container {
		margin-bottom: 1rem;
		margin-right: -0.5rem;
		padding-right: 0.5rem;
	}
</style>
